import type { ReactNode } from 'react'
import { go } from '../../lib/classroom'
import { lang } from '../../lib/words'
import Icon, { type IconName } from '../Icon'
import Pichi from './Pichi'
import { CONTACT } from './Legal'

/**
 * /guia (students) and /guia/maestros (teachers): the hand-held walkthrough.
 *
 * Public, signed in or not, so a teacher can put the link on the projector on
 * day one and a parent can read it at home. Every button named here is named
 * exactly as it appears in the app (src/lib/words.ts, src/lib/i18n.ts); if a
 * label changes there, change it here in the same commit.
 */

type Bi = { es: string; en: string }
type Who = 'students' | 'teachers'

const tx = (b: Bi) => (lang() === 'es' ? b.es : b.en)
/** A button name, shown the way the app shows it. */
const B = ({ icon, children }: { icon?: IconName; children: ReactNode }) => (
  <span className="guide-btn">{icon && <Icon name={icon} size={14} />}{children}</span>
)

interface Step { icon: IconName; color: string; title: Bi; body: () => ReactNode; tip?: Bi }

/* ── Students ────────────────────────────────────────────────────────────── */

const STUDENT_STEPS: Step[] = [
  {
    icon: 'sparkle', color: 'var(--pr-blue)',
    title: { es: 'Abre Adulting 101', en: 'Open Adulting 101' },
    body: () => lang() === 'es'
      ? <p>Instala la app desde la App Store o Google Play, o entra a <b>adulting.studio42.app</b> en cualquier navegador. Es la misma clase en todos lados.</p>
      : <p>Install the app from the App Store or Google Play, or go to <b>adulting.studio42.app</b> in any browser. It is the same class everywhere.</p>,
  },
  {
    icon: 'person', color: 'var(--flamboyan)',
    title: { es: 'Crea tu cuenta (30 segundos)', en: 'Create your account (30 seconds)' },
    body: () => lang() === 'es'
      ? <ol>
          <li>Toca <B icon="sparkle">Soy nuevo</B> si es tu primera vez.</li>
          <li>Escribe tu nombre, como te llama la clase. Te sugerimos un <b>usuario</b>; puedes cambiarlo.</li>
          <li>Toca <B icon="key">Crear mi cuenta</B>.</li>
          <li>Tu teléfono te pide la cara, la huella o su PIN. Eso es todo: ya tienes tu <b>llave de acceso</b>.</li>
        </ol>
      : <ol>
          <li>Tap <B icon="sparkle">I'm new</B> if it's your first time.</li>
          <li>Type your name, what the class calls you. We suggest a <b>username</b>; you can change it.</li>
          <li>Tap <B icon="key">Create my account</B>.</li>
          <li>Your phone asks for your face, fingerprint or its PIN. That's it: you have your <b>passkey</b>.</li>
        </ol>,
    tip: { es: 'No hay contraseña que inventar ni que olvidar. Abajo te explicamos por qué.', en: "There's no password to make up or forget. We explain why below." },
  },
  {
    icon: 'people', color: 'var(--coqui)',
    title: { es: 'Entra a tu clase', en: 'Join your class' },
    body: () => lang() === 'es'
      ? <p>Tu maestro pone un <b>código</b> en la pantalla, como <b>K47</b>. Toca <B icon="people">Entrar a una clase</B> abajo y escríbelo. Si llegaste por el enlace de tu maestro, ya estás adentro.</p>
      : <p>Your teacher puts a <b>code</b> on the screen, like <b>K47</b>. Tap <B icon="people">Join a class</B> at the bottom and type it. If you came in through your teacher's link, you're already in.</p>,
  },
  {
    icon: 'bolt', color: 'var(--pr-red)',
    title: { es: 'Activa los avisos', en: 'Turn on notifications' },
    body: () => lang() === 'es'
      ? <p>Toca la persona arriba <B icon="person">Cuenta</B> y luego <B>Activar notificaciones</B>. Así tu teléfono te avisa cuando la clase empieza.</p>
      : <p>Tap the person at the top <B icon="person">Account</B>, then <B>Turn on notifications</B>. Your phone will tell you when class starts.</p>,
  },
  {
    icon: 'play', color: 'var(--uva)',
    title: { es: 'La clase en vivo', en: 'The live class' },
    body: () => lang() === 'es'
      ? <ul>
          <li>Cuando tu maestro empieza, la app te lleva a la lección sola y te mantiene ahí hasta que termine.</li>
          <li>Contesta en tu teléfono. Puedes cambiar tu respuesta hasta que el maestro cierre la pregunta.</li>
          <li><B icon="question">No sé</B> también cuenta y te da puntos. Decirlo es honesto, y le dice al maestro qué repasar.</li>
          <li>Quizás te llamen al azar. Contestar en voz alta te da un bono.</li>
        </ul>
      : <ul>
          <li>When your teacher starts, the app takes you into the lesson and keeps you there until it ends.</li>
          <li>Answer on your phone. You can change your answer until the teacher closes the question.</li>
          <li><B icon="question">I don't know</B> counts too and earns points. Saying it is honest, and it tells the teacher what to go over again.</li>
          <li>You may be called on at random. Answering out loud earns a bonus.</li>
        </ul>,
  },
  {
    icon: 'book', color: 'var(--mar)',
    title: { es: 'Practica a tu ritmo', en: 'Practise at your own pace' },
    body: () => lang() === 'es'
      ? <p>En la pantalla principal, baja a <b>Practicar</b>. Puedes estudiar todas las lecciones, las que ya pasaron y las que vienen, cuando quieras.</p>
      : <p>On the home screen, scroll to <b>Practice</b>. You can study every lesson, past and upcoming, whenever you want.</p>,
  },
  {
    icon: 'shop', color: 'var(--rosa)',
    title: { es: 'Pichi crece contigo', en: 'Pichi grows with you' },
    body: () => lang() === 'es'
      ? <p>Cada respuesta, cada práctica y cada meta de la vida real que tu maestro verifica te da <b>XP</b>. Subes de nivel, de Huevo a Adulto, ganas <b>insignias</b> de bronce, plata y oro, y gastas tus puntos en <B icon="shop">La Tienda de Pichi</B>. Gastar nunca te baja de nivel. Los puntos no se compran y no son dinero.</p>
      : <p>Every answer, every practice and every real-life milestone your teacher verifies earns <b>XP</b>. You level up from Egg to Adult, earn bronze, silver and gold <b>badges</b>, and spend points in <B icon="shop">Pichi's Shop</B>. Spending never lowers your level. Points can't be bought and aren't money.</p>,
  },
]

/* ── Teachers ────────────────────────────────────────────────────────────── */

const TEACHER_STEPS: Step[] = [
  {
    icon: 'key', color: 'var(--pr-blue)',
    title: { es: 'Tu cuenta de maestro', en: 'Your teacher account' },
    body: () => lang() === 'es'
      ? <p>Crea tu cuenta igual que un estudiante: nombre, usuario y <B icon="key">Crear mi cuenta</B>. Después escríbenos a <a href={`mailto:${CONTACT}`}>{CONTACT}</a> con tu usuario y activamos el <b>modo maestro</b>. Te recomendamos añadir una segunda llave en otro aparato (abajo te decimos cómo).</p>
      : <p>Create your account just like a student: name, username and <B icon="key">Create my account</B>. Then email us at <a href={`mailto:${CONTACT}`}>{CONTACT}</a> with your username and we turn on <b>teacher mode</b>. We recommend adding a second passkey on another device (how, below).</p>,
  },
  {
    icon: 'plus', color: 'var(--flamboyan)',
    title: { es: 'Crea tu clase', en: 'Create your class' },
    body: () => lang() === 'es'
      ? <p>En la pantalla principal, en <b>Nueva clase</b>, ponle nombre (por ejemplo <i>Adulting 11-3</i>) y toca <B icon="plus">Crear</B>. Te damos un código corto, como <b>K47</b>. Cada grupo tiene su propio código.</p>
      : <p>On the home screen, under <b>New class</b>, name it (for example <i>Adulting 11-3</i>) and tap <B icon="plus">Create</B>. You get a short code like <b>K47</b>. Each group gets its own code.</p>,
  },
  {
    icon: 'people', color: 'var(--coqui)',
    title: { es: 'Los estudiantes entran', en: 'Students join' },
    body: () => lang() === 'es'
      ? <ul>
          <li>Abre la clase y proyecta la página: muestra el código y la dirección grande.</li>
          <li>Cada estudiante crea su cuenta y toca <B icon="people">Entrar a una clase</B>. Los verás aparecer en <b>Estudiantes</b>.</li>
          <li>¿Alguien sin teléfono propio, o en una computadora de la escuela? En <B icon="shield">Datos de la clase</B>, usa <B icon="lock">Crear estudiante con PIN</B> y dale su usuario y su PIN.</li>
        </ul>
      : <ul>
          <li>Open the class and put the page on the projector: it shows the code and the address in big type.</li>
          <li>Each student creates an account and taps <B icon="people">Join a class</B>. You'll see them appear under <b>Students</b>.</li>
          <li>Someone without their own phone, or on a school computer? In <B icon="shield">Class data</B>, use <B icon="lock">Add a student with a PIN</B> and give them their username and PIN.</li>
        </ul>,
  },
  {
    icon: 'play', color: 'var(--pr-red)',
    title: { es: 'Da la clase en vivo', en: 'Teach it live' },
    body: () => lang() === 'es'
      ? <ol>
          <li>En la clase, toca <B icon="play">Empezar en vivo</B> en la lección. A cada estudiante le llega un aviso y su teléfono entra a la lección.</li>
          <li>Toca <B icon="screen">Pantalla</B> para abrir la vista del proyector. Tu teléfono o computadora se queda como control.</li>
          <li>Avanza con <B>Siguiente</B>. En cada pregunta, <B>Abrir</B> deja contestar y <B>Cerrar</B> la cierra; <B>Revelar</B> muestra la respuesta.</li>
          <li>Ves en vivo quién contestó qué. <B icon="dice">Llamar al azar</B> escoge a alguien del grupo que quieras.</li>
          <li>Tres "No sé" seguidos marcan a un estudiante en <B icon="refresh">Necesitan repaso</B>, con las preguntas que le costaron.</li>
          <li><B>Terminar lección</B> libera los teléfonos.</li>
        </ol>
      : <ol>
          <li>In the class, tap <B icon="play">Start live</B> on the lesson. Every student gets a notification and their phone goes into the lesson.</li>
          <li>Tap <B icon="screen">Projector</B> to open the projector view. Your phone or laptop stays as the remote.</li>
          <li>Move on with <B>Next</B>. On each question, <B>Open</B> lets students answer and <B>Close</B> closes it; <B>Reveal</B> shows the answer.</li>
          <li>You see live who answered what. <B icon="dice">Call at random</B> picks someone from whichever group you choose.</li>
          <li>Three "I don't know" in a row flag a student under <B icon="refresh">Need a reteach</B>, with the questions they struggled on.</li>
          <li><B>End lesson</B> releases the phones.</li>
        </ol>,
  },
  {
    icon: 'trophy', color: 'var(--uva)',
    title: { es: 'Metas y puntos', en: 'Milestones and points' },
    body: () => lang() === 'es'
      ? <p>Toca a un estudiante en la lista. Ahí verificas sus <b>metas de la vida real</b> (te enseña el app del banco, su presupuesto) y le das o quitas puntos con una razón. Todo queda anotado.</p>
      : <p>Tap a student in the list. There you verify their <b>real-life milestones</b> (they show you the bank app, their budget) and give or take back points with a reason. Everything is recorded.</p>,
  },
  {
    icon: 'shield', color: 'var(--mar)',
    title: { es: 'Los datos de tu clase', en: "Your class's data" },
    body: () => lang() === 'es'
      ? <ul>
          <li><B icon="download">Exportar registro (CSV)</B>: una fila por estudiante, lista para tu registro de notas.</li>
          <li><B icon="trash">Sacar de la clase</B> (al tocar al estudiante): borra sus respuestas de esta clase.</li>
          <li><B icon="trash">Borrar la clase</B>: borra todas sus lecciones y respuestas. Los estudiantes mantienen su cuenta.</li>
          <li>Sin anuncios, sin rastreo, sin mensajes entre estudiantes. Detalles en <a href="/privacy" onClick={(e) => { e.preventDefault(); go('/privacy') }}>Privacidad</a>.</li>
        </ul>
      : <ul>
          <li><B icon="download">Export record (CSV)</B>: one row per student, ready for your gradebook.</li>
          <li><B icon="trash">Remove from class</B> (tap the student): deletes their answers in this class.</li>
          <li><B icon="trash">Delete the class</B>: deletes all its lessons and answers. Students keep their accounts.</li>
          <li>No ads, no tracking, no messages between students. Details in <a href="/privacy" onClick={(e) => { e.preventDefault(); go('/privacy') }}>Privacy</a>.</li>
        </ul>,
  },
]

/* ── Where everything is ─────────────────────────────────────────────────── */

const MAP: { icon: IconName; label: Bi; what: Bi; teacher?: boolean }[] = [
  { icon: 'home', label: { es: 'Pichi', en: 'Pichi' }, what: { es: 'Arriba a la izquierda: vuelve a la pantalla principal.', en: 'Top left: back to the home screen.' } },
  { icon: 'volume', label: { es: 'Sonido', en: 'Sound' }, what: { es: 'Prende o apaga los sonidos.', en: 'Sound effects on or off.' } },
  { icon: 'globe', label: { es: 'Banderas', en: 'Flags' }, what: { es: 'Español o inglés, cuando quieras.', en: 'Spanish or English, any time.' } },
  { icon: 'person', label: { es: 'Cuenta', en: 'Account' }, what: { es: 'Tus llaves, avisos y borrar tu cuenta.', en: 'Your passkeys, notifications and deleting your account.' } },
  { icon: 'door', label: { es: 'Salir', en: 'Sign out' }, what: { es: 'Cierra tu sesión en este aparato.', en: 'Signs you out on this device.' } },
  { icon: 'people', label: { es: 'Entrar a una clase', en: 'Join a class' }, what: { es: 'Abajo: escribe el código de tu maestro.', en: "Bottom: type your teacher's code." } },
  { icon: 'shop', label: { es: 'La Tienda', en: 'The Shop' }, what: { es: 'Abajo: viste a Pichi con tus puntos.', en: 'Bottom: dress Pichi with your points.' } },
  { icon: 'bulb', label: { es: 'Comentarios', en: 'Feedback' }, what: { es: 'Abajo: dinos qué mejorar. Lo leemos todo.', en: 'Bottom: tell us what to improve. We read everything.' } },
]

/* ── The page ────────────────────────────────────────────────────────────── */

export default function Guide({ who }: { who: Who }) {
  const es = lang() === 'es'
  const steps = who === 'students' ? STUDENT_STEPS : TEACHER_STEPS
  return (
    <div className="guide">
      <div className="guide-hero">
        <Pichi size={120} stage={2} mood="wave" />
        <div>
          <p className="tagline">{es ? 'Paso a paso, con Pichi' : 'Step by step, with Pichi'}</p>
          <h1>{who === 'students' ? (es ? 'Guía para estudiantes' : 'Student guide') : (es ? 'Guía para maestros' : 'Teacher guide')}</h1>
        </div>
      </div>

      <div className="mode-switch guide-tabs" role="tablist">
        <button role="tab" aria-selected={who === 'students'} className={who === 'students' ? 'on' : ''} onClick={() => go('/guia')}>
          <Icon name="person" /> {es ? 'Estudiantes' : 'Students'}
        </button>
        <button role="tab" aria-selected={who === 'teachers'} className={who === 'teachers' ? 'on' : ''} onClick={() => go('/guia/maestros')}>
          <Icon name="screen" /> {es ? 'Maestros' : 'Teachers'}
        </button>
      </div>

      <ol className="guide-steps">
        {steps.map((s, i) => (
          <li key={i} className="guide-step" style={{ ['--step' as string]: s.color }}>
            <span className="guide-num" aria-hidden="true">{i + 1}</span>
            <div>
              <h2><Icon name={s.icon} /> {tx(s.title)}</h2>
              {s.body()}
              {s.tip && <p className="guide-tip"><Icon name="bulb" size={16} /> {tx(s.tip)}</p>}
            </div>
          </li>
        ))}
      </ol>

      <Passkeys />

      {who === 'students' && (
        <section className="card">
          <h2><Icon name="info" /> {es ? '¿Dónde está todo?' : 'Where is everything?'}</h2>
          <ul className="guide-map">
            {MAP.map((m) => (
              <li key={m.icon}>
                <span className="guide-chip"><Icon name={m.icon} /></span>
                <span><b>{tx(m.label)}</b><br /><span className="muted small">{tx(m.what)}</span></span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="card">
        <h2><Icon name="question" /> {es ? 'Si algo pasa' : 'If something goes wrong'}</h2>
        <ul>
          {es ? <>
            <li><b>Cambié de teléfono.</b> Si tu llave se guarda en iCloud o en Google (casi siempre), entra en el teléfono nuevo con tu misma cuenta y ahí está.</li>
            <li><b>Quiero entrar en otra computadora.</b> Toca <B icon="key">Ya tengo cuenta</B>; tu navegador te deja usar tu teléfono como llave con un código QR.</li>
            <li><b>Perdí el acceso.</b> Tu maestro te puede ayudar, o escríbenos a <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.</li>
            <li><b>Mi maestro me dio un PIN.</b> En la primera pantalla toca <B icon="lock">Entrar con PIN</B>.</li>
          </> : <>
            <li><b>I got a new phone.</b> If your passkey is saved to iCloud or Google (it almost always is), sign in on the new phone with the same account and it's there.</li>
            <li><b>I want to sign in on another computer.</b> Tap <B icon="key">I have an account</B>; your browser lets you use your phone as the key with a QR code.</li>
            <li><b>I lost access.</b> Your teacher can help, or write to us at <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.</li>
            <li><b>My teacher gave me a PIN.</b> On the first screen tap <B icon="lock">Sign in with a PIN</B>.</li>
          </>}
        </ul>
      </section>

      <p className="guide-cta"><button className="primary big" onClick={() => go('/')}><Icon name="sparkle" /> {es ? '¡Vamos!' : "Let's go!"}</button></p>
    </div>
  )
}

/** Why a passkey: the padlock and the key. */
function Passkeys() {
  const es = lang() === 'es'
  return (
    <section className="card guide-passkeys">
      <h2><Icon name="shield" /> {es ? '¿Por qué una llave de acceso y no una contraseña?' : 'Why a passkey and not a password?'}</h2>
      <p>{es
        ? 'Piensa en un candado y su llave. Cuando creas tu cuenta, tu teléfono hace las dos cosas a la vez:'
        : 'Think of a padlock and its key. When you create your account, your phone makes both at once:'}</p>
      <div className="guide-lockkey">
        <div className="guide-lk" style={{ ['--step' as string]: 'var(--pr-blue)' }}>
          <span className="guide-chip big"><Icon name="lock" size={30} /></span>
          <b>{es ? 'El candado' : 'The padlock'}</b>
          <span className="small">{es ? 'Nos lo da a nosotros. Solo sirve para cerrar, no para abrir.' : 'It gives it to us. It can only lock, never unlock.'}</span>
        </div>
        <div className="guide-lk" style={{ ['--step' as string]: 'var(--flamboyan)' }}>
          <span className="guide-chip big"><Icon name="key" size={30} /></span>
          <b>{es ? 'La llave' : 'The key'}</b>
          <span className="small">{es ? 'Se queda en tu teléfono. Nunca sale de ahí, ni a nosotros.' : 'It stays on your phone. It never leaves, not even to us.'}</span>
        </div>
      </div>
      <ul className="guide-why">
        {((es ? [
          ['eye', 'Nada que adivinar', 'No hay contraseña, así que nadie la puede adivinar, ver por encima de tu hombro ni copiar de un papelito.'],
          ['shield', 'Nada que robar', 'Nosotros solo guardamos candados. Si alguien entrara a nuestros servidores, un candado no abre nada.'],
          ['alert', 'A prueba de trampas', 'Tu llave solo funciona en adulting.studio42.app. Una página falsa que imite la nuestra no puede usarla.'],
          ['hand', 'Tu cara se queda contigo', 'La cara o la huella solo despiertan la llave dentro de tu teléfono. Nunca nos llegan.'],
          ['bolt', 'Más rápido', 'Un toque y tu cara. Nada que escribir, nada que olvidar, nada que cambiar cada tres meses.'],
        ] : [
          ['eye', 'Nothing to guess', "There's no password, so nobody can guess it, read it over your shoulder or copy it off a sticky note."],
          ['shield', 'Nothing to steal', 'We only keep padlocks. If someone broke into our servers, a padlock opens nothing.'],
          ['alert', 'Scam-proof', 'Your key only works on adulting.studio42.app. A fake page that looks like ours cannot use it.'],
          ['hand', 'Your face stays with you', 'Your face or fingerprint only wakes the key up inside your phone. It never reaches us.'],
          ['bolt', 'Faster', 'One tap and your face. Nothing to type, nothing to forget, nothing to change every three months.'],
        ]) as [IconName, string, string][]).map(([icon, title, body]) => (
          <li key={title}><span className="guide-chip"><Icon name={icon} /></span><span><b>{title}.</b> {body}</span></li>
        ))}
      </ul>
      <p className="guide-tip"><Icon name="key" size={16} /> {es
        ? 'Consejo: en Cuenta, toca "Añadir un passkey" en un segundo aparato (tu tablet o la computadora de tu casa). Así siempre tienes otra llave.'
        : 'Tip: in Account, tap "Add a passkey" on a second device (your tablet or home computer). That way you always have a spare key.'}</p>
    </section>
  )
}
